import React, { useState, useRef, useEffect, useMemo } from 'react';
import styled, { css } from 'styled-components';
import { Clock } from 'lucide-react';
import { useTranslation } from 'react-i18next';

export default function TimeInput({ value, onChange, label, required, error }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const hours = value ? value.split(':')[0] || '' : '';
  const minutes = value ? value.split(':')[1] || '' : '';
  const hasValue = !!value;
  const focused = open;

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleHour = (h) => {
    const m = minutes || '00';
    onChange(`${h}:${m}`);
  };

  const handleMinute = (m) => {
    const h = hours || '00';
    onChange(`${h}:${m}`);
    setOpen(false);
  };

  const hourList = useMemo(() => Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0')), []);
  const minuteList = useMemo(() => ['00', '05', '10', '15', '20', '25', '30', '35', '40', '45', '50', '55'], []);

  return (
    <Wrapper ref={ref}>
      <InputBox onClick={() => setOpen(!open)} $focused={focused} $error={!!error}>
        <FloatingLabel $float={focused || hasValue} $error={!!error}>
          {label}
          {required && <RequiredMark>*</RequiredMark>}
        </FloatingLabel>
        <DisplayText $hasValue={hasValue}>
          {hasValue ? `${hours}:${minutes}` : ''}
        </DisplayText>
        <IconWrap><Clock size={16} /></IconWrap>
      </InputBox>

      {open && (
        <Dropdown>
          <DropdownCol>
            <ColTitle>{t('common.hour')}</ColTitle>
            <ColList>
              {hourList.map(h => (
                <ColItem key={h} $selected={h === hours} onClick={() => handleHour(h)}>{h}</ColItem>
              ))}
            </ColList>
          </DropdownCol>
          <DropdownCol>
            <ColTitle>{t('common.minute')}</ColTitle>
            <ColList>
              {minuteList.map(m => (
                <ColItem key={m} $selected={m === minutes} onClick={() => handleMinute(m)}>{m}</ColItem>
              ))}
            </ColList>
          </DropdownCol>
        </Dropdown>
      )}

      {error && <ErrorText>{error}</ErrorText>}
    </Wrapper>
  );
}

const Wrapper = styled.div`
  position: relative;
  width: 100%;
`;

const InputBox = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  height: 48px;
  padding: 0 14px;
  border: 1.5px solid ${({ $error, $focused }) => $error ? '#E53935' : $focused ? '#0291EF' : '#E0E4E8'};
  border-radius: 4px;
  background: #fff;
  cursor: pointer;
  transition: border-color 0.2s, box-shadow 0.2s;
  box-shadow: ${({ $focused }) => $focused ? '0 0 0 3px rgba(2, 145, 239, 0.1)' : 'none'};

  &:hover {
    border-color: ${({ $error, $focused }) => $error ? '#E53935' : $focused ? '#0291EF' : '#C0C8D0'};
  }
`;

const FloatingLabel = styled.label`
  position: absolute;
  left: 14px;
  transition: all 0.2s ease;
  pointer-events: none;
  font-family: 'Nunito Sans', sans-serif;
  background: #fff;
  padding: 0 4px;

  ${({ $float, $error }) =>
    $float
      ? css`
          top: -8px;
          font-size: 11px;
          color: ${$error ? '#E53935' : '#0291EF'};
          font-weight: 600;
        `
      : css`
          top: 50%;
          transform: translateY(-50%);
          font-size: 14px;
          color: #A0A8B4;
          font-weight: 400;
        `}
`;

const RequiredMark = styled.span`
  color: #E53935;
  margin-left: 2px;
`;

const DisplayText = styled.span`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 14px;
  color: ${({ $hasValue }) => $hasValue ? '#004242' : 'transparent'};
  flex: 1;
`;

const IconWrap = styled.span`
  display: flex;
  color: #A0A8B4;
  flex-shrink: 0;
`;

const Dropdown = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  margin-top: 4px;
  background: #fff;
  border: 1px solid #E8ECF0;
  border-radius: 6px;
  box-shadow: 0 8px 24px rgba(0, 66, 66, 0.12);
  display: flex;
  z-index: 100;
  overflow: hidden;
`;

const DropdownCol = styled.div`
  flex: 1;
  &:first-child { border-right: 1px solid #f1f5f9; }
`;

const ColTitle = styled.div`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 11px;
  font-weight: 700;
  color: #64748b;
  text-align: center;
  padding: 8px 0 4px;
  border-bottom: 1px solid #f1f5f9;
`;

const ColList = styled.div`
  max-height: 200px;
  overflow-y: auto;
  &::-webkit-scrollbar { width: 4px; }
  &::-webkit-scrollbar-thumb { background: #E0E4E8; border-radius: 4px; }
`;

const ColItem = styled.div`
  padding: 6px 0;
  text-align: center;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 13px;
  color: ${({ $selected }) => $selected ? '#0291EF' : '#004242'};
  font-weight: ${({ $selected }) => $selected ? '700' : '400'};
  background: ${({ $selected }) => $selected ? 'rgba(2, 145, 239, 0.06)' : 'transparent'};
  cursor: pointer;
  &:hover { background: rgba(2, 145, 239, 0.04); }
`;

const ErrorText = styled.span`
  display: block;
  margin-top: 4px;
  font-size: 11px;
  font-family: 'Nunito Sans', sans-serif;
  color: #E53935;
`;
